import { useCallback, useEffect, useState } from "react";
import { Recycle, Tag, ShieldAlert, Plus, Check, X, Sparkles, Repeat2 } from "lucide-react";
import type { ScrapRequest, SprRequest, VoidTransactionRecord, FraudFlag } from "@sbd-tcic/shared-types";
import { api, ApiClientError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast } from "../lib/toast";
import { StatusBadge } from "../components/StatusBadge";
import { DataTable, type Column } from "../components/DataTable";

interface CustomerRiskRow {
  customerId: string;
  customerName: string;
  totalOrders: number;
  riskOrderCount: number;
  ratePct: number;
}
interface RepeatSerialRow {
  serialNumber: string;
  modelNumber: string;
  repairs: { id: string; customerId: string; createdDate: string }[];
}

/** Same class pattern as StatusBadge (DS-CMP-003) — a real, code-computed rate drives the color,
 *  never a hardcoded label, but the token set stays identical to every other severity indicator
 *  in the app. */
function RiskRateBadge({ ratePct }: { ratePct: number }) {
  const color = ratePct >= 50 ? "red" : "orange";
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold bg-status-${color}-bg text-status-${color}`}>{ratePct}%</span>;
}

type Tab = "Scrap Requests" | "Special Pricing Requests" | "Void & Fraud Review";
const TABS: Tab[] = ["Scrap Requests", "Special Pricing Requests", "Void & Fraud Review"];

export function Oversight() {
  const { can } = useAuth();
  const { toast } = useToast();

  const [tab, setTab] = useState<Tab>("Scrap Requests");

  const canApprovePricing = can("management_oversight.approve_pricing");
  const canApproveSpr = can("management_oversight.approve_spr");
  const canReviewVoidFraud = can("management_oversight.review_void_fraud");

  // --- Scrap requests ---
  const [scrapRequests, setScrapRequests] = useState<ScrapRequest[]>([]);
  const [scrapLoading, setScrapLoading] = useState(true);
  const [scrapBusyId, setScrapBusyId] = useState<string | null>(null);

  const loadScrap = useCallback(async () => {
    setScrapLoading(true);
    try {
      setScrapRequests(await api.get<ScrapRequest[]>("/oversight/scrap-requests"));
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to load scrap requests.", "error");
    } finally {
      setScrapLoading(false);
    }
  }, [toast]);

  async function decideScrap(id: string, decision: "Approved" | "Rejected") {
    setScrapBusyId(id);
    try {
      await api.post(`/oversight/scrap-requests/${id}/decide`, { decision });
      toast(`Scrap request ${decision.toLowerCase()}.`, "success");
      await loadScrap();
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to record decision.", "error");
    } finally {
      setScrapBusyId(null);
    }
  }

  // --- Special Pricing Requests ---
  const [sprRequests, setSprRequests] = useState<SprRequest[]>([]);
  const [sprLoading, setSprLoading] = useState(true);
  const [sprBusyId, setSprBusyId] = useState<string | null>(null);
  const [sprExpiration, setSprExpiration] = useState<Record<string, string>>({});

  const [newSprCustomerId, setNewSprCustomerId] = useState("");
  const [newSprDiscount, setNewSprDiscount] = useState("");
  const [creatingSpr, setCreatingSpr] = useState(false);

  const loadSpr = useCallback(async () => {
    setSprLoading(true);
    try {
      setSprRequests(await api.get<SprRequest[]>("/oversight/spr-requests"));
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to load special pricing requests.", "error");
    } finally {
      setSprLoading(false);
    }
  }, [toast]);

  async function createSpr() {
    if (!newSprDiscount.trim()) return;
    setCreatingSpr(true);
    try {
      await api.post("/oversight/spr-requests", { discount: newSprDiscount.trim(), customerId: newSprCustomerId.trim() || undefined });
      toast("Special pricing request submitted.", "success");
      setNewSprDiscount("");
      setNewSprCustomerId("");
      await loadSpr();
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to submit request.", "error");
    } finally {
      setCreatingSpr(false);
    }
  }

  async function decideSpr(id: string, decision: "Approved" | "Rejected") {
    setSprBusyId(id);
    try {
      await api.post(`/oversight/spr-requests/${id}/decide`, {
        decision,
        expiration: sprExpiration[id]?.trim() || undefined,
      });
      toast(`Special pricing request ${decision.toLowerCase()}.`, "success");
      await loadSpr();
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to record decision.", "error");
    } finally {
      setSprBusyId(null);
    }
  }

  // --- Void / fraud review ---
  const [voids, setVoids] = useState<VoidTransactionRecord[]>([]);
  const [fraudFlags, setFraudFlags] = useState<FraudFlag[]>([]);
  const [voidFraudLoading, setVoidFraudLoading] = useState(true);
  const [fraudBusyId, setFraudBusyId] = useState<string | null>(null);

  const loadVoidFraud = useCallback(async () => {
    setVoidFraudLoading(true);
    try {
      const data = await api.get<{ voids: VoidTransactionRecord[]; fraudFlags: FraudFlag[] }>("/oversight/void-fraud");
      setVoids(data.voids);
      setFraudFlags(data.fraudFlags);
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to load void & fraud data.", "error");
    } finally {
      setVoidFraudLoading(false);
    }
  }, [toast]);

  async function reviewFraud(id: string, decision: "Confirmed" | "Dismissed") {
    setFraudBusyId(id);
    try {
      await api.post(`/oversight/fraud-flags/${id}/review`, { decision });
      toast(`Fraud flag ${decision.toLowerCase()}.`, "success");
      await loadVoidFraud();
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to record review.", "error");
    } finally {
      setFraudBusyId(null);
    }
  }

  // --- AI Fraud & Warranty Risk (real detection, AI-narrated) ---
  const [customerRisk, setCustomerRisk] = useState<CustomerRiskRow[]>([]);
  const [repeatSerials, setRepeatSerials] = useState<RepeatSerialRow[]>([]);
  const [riskSignalsLoading, setRiskSignalsLoading] = useState(true);
  const [riskBriefing, setRiskBriefing] = useState<string | null>(null);
  const [riskBriefingBusy, setRiskBriefingBusy] = useState(false);
  const [riskBriefingError, setRiskBriefingError] = useState<string | null>(null);

  const loadRiskSignals = useCallback(async () => {
    setRiskSignalsLoading(true);
    try {
      const data = await api.get<{ customerRisk: CustomerRiskRow[]; repeatSerials: RepeatSerialRow[] }>("/oversight/risk-signals");
      setCustomerRisk(data.customerRisk);
      setRepeatSerials(data.repeatSerials);
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to load risk signals.", "error");
    } finally {
      setRiskSignalsLoading(false);
    }
  }, [toast]);

  async function generateRiskBriefing() {
    setRiskBriefingBusy(true);
    setRiskBriefingError(null);
    try {
      const { briefing } = await api.post<{ briefing: string }>("/oversight/risk-briefing");
      setRiskBriefing(briefing);
    } catch (err) {
      setRiskBriefingError(err instanceof ApiClientError ? err.message : "Unable to generate a risk briefing right now.");
    } finally {
      setRiskBriefingBusy(false);
    }
  }

  useEffect(() => {
    if (tab === "Scrap Requests") void loadScrap();
    if (tab === "Special Pricing Requests") void loadSpr();
    if (tab === "Void & Fraud Review") {
      void loadVoidFraud();
      void loadRiskSignals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-1.5 bg-surface rounded-md p-1 w-fit max-w-full overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded text-xs font-bold whitespace-nowrap transition-colors ${
              tab === t ? "bg-brand-blue text-white" : "text-ink-muted hover:text-ink"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Scrap Requests" && (
        <div className="card p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-3.5 flex items-center gap-2">
            <Recycle size={15} /> Scrap Requests
          </h2>
          {scrapLoading ? (
            <div className="text-sm text-ink-muted text-center py-8">Loading scrap requests…</div>
          ) : scrapRequests.length === 0 ? (
            <div className="text-sm text-ink-muted text-center py-8">No scrap requests found.</div>
          ) : (
            <div className="flex flex-col gap-2">
              {scrapRequests.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 border border-border rounded-md px-3.5 py-2.5 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm">
                      {r.partNumber} <span className="text-ink-muted font-normal">— {r.reason}</span>
                    </div>
                    <div className="text-xs text-ink-muted">
                      Requested by {r.requestedBy}
                      {r.approvedBy ? ` · decided by ${r.approvedBy}` : ""}
                    </div>
                  </div>
                  <StatusBadge status={r.status} />
                  {canApprovePricing && r.status === "Pending" && (
                    <div className="flex gap-1.5 flex-shrink-0">
                      <button className="btn-outline" disabled={scrapBusyId === r.id} onClick={() => void decideScrap(r.id, "Approved")}>
                        <Check size={14} /> Approve
                      </button>
                      <button className="btn-danger" disabled={scrapBusyId === r.id} onClick={() => void decideScrap(r.id, "Rejected")}>
                        <X size={14} /> Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "Special Pricing Requests" && (
        <div className="flex flex-col gap-5">
          <div className="card p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-3.5 flex items-center gap-2">
              <Plus size={15} /> New Special Pricing Request
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div className="sm:col-span-1">
                <label className="field-label">Customer ID (optional)</label>
                <input className="input" value={newSprCustomerId} onChange={(e) => setNewSprCustomerId(e.target.value)} placeholder="Customer ID" />
              </div>
              <div className="sm:col-span-1">
                <label className="field-label">Discount</label>
                <input className="input" value={newSprDiscount} onChange={(e) => setNewSprDiscount(e.target.value)} placeholder="e.g. 15%" />
              </div>
              <button className="btn-primary justify-center" disabled={creatingSpr || !newSprDiscount.trim()} onClick={() => void createSpr()}>
                {creatingSpr ? "Submitting…" : "Submit Request"}
              </button>
            </div>
          </div>

          <div className="card p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-3.5 flex items-center gap-2">
              <Tag size={15} /> Special Pricing Requests
            </h2>
            {sprLoading ? (
              <div className="text-sm text-ink-muted text-center py-8">Loading requests…</div>
            ) : sprRequests.length === 0 ? (
              <div className="text-sm text-ink-muted text-center py-8">No special pricing requests found.</div>
            ) : (
              <div className="flex flex-col gap-2">
                {sprRequests.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 border border-border rounded-md px-3.5 py-2.5 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm">
                        Discount {r.discount} <span className="text-ink-muted font-normal">{r.customerId ? `· customer ${r.customerId}` : ""}</span>
                      </div>
                      <div className="text-xs text-ink-muted">
                        Requested by {r.requestedBy}
                        {r.approvedBy ? ` · decided by ${r.approvedBy}` : ""}
                        {r.expiration ? ` · expires ${r.expiration}` : ""}
                      </div>
                    </div>
                    <StatusBadge status={r.status} />
                    {canApproveSpr && r.status === "Pending" && (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <input
                          type="date"
                          className="input w-36"
                          value={sprExpiration[r.id] ?? ""}
                          onChange={(e) => setSprExpiration((prev) => ({ ...prev, [r.id]: e.target.value }))}
                          title="Optional expiration date (used on approval)"
                        />
                        <button className="btn-outline" disabled={sprBusyId === r.id} onClick={() => void decideSpr(r.id, "Approved")}>
                          <Check size={14} /> Approve
                        </button>
                        <button className="btn-danger" disabled={sprBusyId === r.id} onClick={() => void decideSpr(r.id, "Rejected")}>
                          <X size={14} /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "Void & Fraud Review" && (
        <div className="flex flex-col gap-5">
          <div className="card p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
              <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted flex items-center gap-2">
                <Sparkles size={15} /> AI Risk Briefing
              </h2>
              <button className="btn-outline" disabled={riskBriefingBusy} onClick={() => void generateRiskBriefing()}>
                <Sparkles size={14} /> {riskBriefingBusy ? "Generating…" : "Generate Briefing"}
              </button>
            </div>
            <p className="text-xs text-ink-muted mb-2">
              Narrates real return/warranty/refund rates and repeat-serial repair patterns below — flagged for review, never a fraud determination.
            </p>
            {riskBriefingError && <p className="text-xs font-semibold text-status-red">{riskBriefingError}</p>}
            {riskBriefing && <p className="text-sm bg-surface-muted rounded-lg p-3.5 mt-1 whitespace-pre-line">{riskBriefing}</p>}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="card overflow-hidden">
              <div className="px-5 pt-5 pb-1">
                <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted">Customer Return/Warranty Rate</h2>
                <p className="text-xs text-ink-muted mt-1 mb-2">Customers with at least 2 real orders where some are returns, warranty exchanges, or refunds.</p>
              </div>
              <DataTable<CustomerRiskRow>
                columns={
                  [
                    { key: "customer", header: "Customer", render: (r) => r.customerName },
                    { key: "risk", header: "Risk Orders", render: (r) => `${r.riskOrderCount} of ${r.totalOrders}` },
                    { key: "rate", header: "Rate", render: (r) => <RiskRateBadge ratePct={r.ratePct} /> },
                  ] satisfies Column<CustomerRiskRow>[]
                }
                rows={customerRisk}
                rowKey={(r) => r.customerId}
                emptyMessage={riskSignalsLoading ? "Loading…" : "No customer currently shows an elevated return/warranty rate."}
              />
            </div>

            <div className="card overflow-hidden">
              <div className="px-5 pt-5 pb-1">
                <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted flex items-center gap-2">
                  <Repeat2 size={15} /> Repeat-Serial Repairs
                </h2>
                <p className="text-xs text-ink-muted mt-1 mb-2">The same physical tool serviced on more than one repair order.</p>
              </div>
              {riskSignalsLoading ? (
                <div className="text-sm text-ink-muted text-center py-8">Loading…</div>
              ) : repeatSerials.length === 0 ? (
                <div className="text-sm text-ink-muted text-center py-8">No serial number currently appears on more than one repair order.</div>
              ) : (
                <div className="flex flex-col gap-2 px-5 pb-5">
                  {repeatSerials.map((r) => (
                    <div key={r.serialNumber} className="border border-border rounded-md px-3.5 py-2.5 text-sm">
                      <div className="font-semibold">
                        {r.serialNumber} <span className="text-ink-muted font-normal">— {r.modelNumber}</span>
                      </div>
                      <div className="text-xs text-ink-muted">Repair orders: {r.repairs.map((rep) => rep.id).join(", ")}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="card p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-3.5">Void Transactions</h2>
            {voidFraudLoading ? (
              <div className="text-sm text-ink-muted text-center py-8">Loading voids…</div>
            ) : voids.length === 0 ? (
              <div className="text-sm text-ink-muted text-center py-8">No void transactions on record.</div>
            ) : (
              <div className="flex flex-col gap-2">
                {voids.map((v) => (
                  <div key={v.id} className="border border-border rounded-md px-3.5 py-2.5 text-sm">
                    <div className="font-semibold">{v.transactionRef}</div>
                    <div className="text-xs text-ink-muted">
                      {v.reasonCode} · approved by {v.approvedBy} · {v.createdAt.slice(0, 10)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-3.5 flex items-center gap-2">
              <ShieldAlert size={15} /> Fraud Flags
            </h2>
            {voidFraudLoading ? (
              <div className="text-sm text-ink-muted text-center py-8">Loading fraud flags…</div>
            ) : fraudFlags.length === 0 ? (
              <div className="text-sm text-ink-muted text-center py-8">No fraud flags on record.</div>
            ) : (
              <div className="flex flex-col gap-2">
                {fraudFlags.map((f) => (
                  <div key={f.id} className="flex items-center justify-between gap-3 border border-border rounded-md px-3.5 py-2.5 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm">{f.transactionRef}</div>
                      <div className="text-xs text-ink-muted">
                        {f.ruleMatched} · {f.createdAt.slice(0, 10)}
                      </div>
                    </div>
                    <StatusBadge status={f.status} />
                    {canReviewVoidFraud && (f.status === "Open" || f.status === "Reviewed") && (
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button className="btn-danger" disabled={fraudBusyId === f.id} onClick={() => void reviewFraud(f.id, "Confirmed")}>
                          Confirm
                        </button>
                        <button className="btn-outline" disabled={fraudBusyId === f.id} onClick={() => void reviewFraud(f.id, "Dismissed")}>
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
